import { useSelector, useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useEffect } from 'react'
import {
  Drawer,
  Box,
  IconButton,
  Divider,
  Tooltip,
  useMediaQuery,
  useTheme
} from '@mui/material'
import { LogoutOutlined, Menu as MenuIcon } from '@mui/icons-material'
import NavList from './NavList'
import Logo from '../../Logo'
import { toggle } from '../../../actions/sidebar'
import { logoutUser } from '../../../actions/user'

export const DRAWER_WIDTH = 248
export const MINI_WIDTH = 56

const paperSx = (width, theme, active) => ({
  width,
  top: 0,
  padding: 0,
  backgroundColor: '#0f0f0f',
  borderRight: 'none',
  overflowX: 'hidden',
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: active
      ? theme.transitions.duration.enteringScreen
      : theme.transitions.duration.leavingScreen
  })
})

const Sidebar = () => {
  const { active, show } = useSelector(state => state.ui.sidebar)
  const dispatch = useDispatch()
  const history = useHistory()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  useEffect(() => {
    if (isMobile && active) dispatch(toggle(false))
  }, [isMobile]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!show) return null

  const onLogout = () => {
    dispatch(logoutUser())
    history.push('/login')
  }

  const drawerContent = (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', cursor: active ? 'default' : 'pointer' }}
      onClick={!active ? () => dispatch(toggle(true)) : undefined}
    >
      {/* ── Header ─────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: 88,
          px: '8px',
          gap: 1,
          flexShrink: 0
        }}
      >
        <IconButton
          onClick={(e) => { e.stopPropagation(); dispatch(toggle(!active)) }}
          sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.08)' } }}
        >
          <MenuIcon />
        </IconButton>
        {active && (
          <Box
            onClick={() => history.push('/')}
            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', height: 56 }}
          >
            <Logo width='150px' height='48px' />
          </Box>
        )}
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mx: '8px' }} />

      {/* ── Nav items ──────────────────────────────────────── */}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', py: '8px' }}>
        <NavList />
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mx: '8px' }} />

      {/* ── Logout ─────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: '10px', flexShrink: 0 }}>
        <Tooltip title='Logout' placement={active ? 'top' : 'right'}>
          <IconButton
            size='small'
            onClick={onLogout}
            sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.08)' } }}
          >
            <LogoutOutlined sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  )

  if (isMobile) {
    return (
      <Drawer
        variant='temporary'
        open={active}
        onClose={() => dispatch(toggle(false))}
        ModalProps={{ keepMounted: true }}
        sx={{ '& .MuiDrawer-paper': paperSx(DRAWER_WIDTH, theme, active) }}
      >
        {drawerContent}
      </Drawer>
    )
  }

  return (
    <Drawer
      variant='permanent'
      sx={{
        width: active ? DRAWER_WIDTH : MINI_WIDTH,
        flexShrink: 0,
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: active
            ? theme.transitions.duration.enteringScreen
            : theme.transitions.duration.leavingScreen
        }),
        '& .MuiDrawer-paper': paperSx(active ? DRAWER_WIDTH : MINI_WIDTH, theme, active)
      }}
    >
      {drawerContent}
    </Drawer>
  )
}

export default Sidebar
